import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import CourseCreator from "./CourseCreator";
import Dialog from "./Dialog";
import { useToast } from "../providers/ToastProvider";
import api from "../../services/api";

interface ManagedCourse {
  id: string;
  title: string;
  topics: string[];
  enrollmentCount: number;
  completionCount: number;
  tasksCount?: number;
  status: "DRAFT" | "PUBLISHED";
  publishDate: string;
  description?: string;
  videoLink?: string;
  highlightsLink?: string;
  categoryId?: string;
  category?: { id: string; name: string } | null;
  learning_order?: number;
  estimated_duration_minutes?: number;
  difficulty_level?: string;
  what_youll_learn?: string[];
  feedback_links?: string[];
  access_tags?: number[];
}

const formatDuration = (minutes: number) => {
  if (!minutes) return "";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0 && m > 0) return `${h} hr ${m}m`;
  if (h > 0) return `${h} hr`;
  return `${m}m`;
};

export default function CourseManager() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState<ManagedCourse[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isCreatorOpen, setIsCreatorOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState<ManagedCourse | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCourses, setTotalCourses] = useState(0);
  
  const [plans, setPlans] = useState<any[]>([]);
  const [filterTier, setFilterTier] = useState<string>("ALL");
  const { showToast } = useToast();

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await api.get('/admin/subscription-plans');
        setPlans(res.data);
      } catch (err) {
        console.error("Failed to fetch plans:", err);
      }
    };
    fetchPlans();
  }, []);

  const fetchCourses = async (page = 1, search = "", tier = "ALL") => {
    try {
      let url = `/coach/courses-manager?page=${page}&search=${encodeURIComponent(search)}`;
      if (tier !== "ALL") {
        url += `&tier=${tier}`;
      }
      const res = await api.get(url);
      const data = res.data.data && Array.isArray(res.data.data) ? res.data.data : [];
      
      const normalized = data.map((c: any) => ({
        id: c.id,
        title: c.title,
        description: c.description,
        videoLink: c.videoLink || "",
        highlightsLink: c.highlightsLink || "",

        topics: c.topics?.map((t: any) => t.name) || [],
        status: (c.isPublished ? "PUBLISHED" : "DRAFT") as "PUBLISHED" | "DRAFT",
        publishDate: c.publishDate ? new Date(c.publishDate).toISOString().split('T')[0] : "",
        enrollmentCount: c.enrollmentCount || 0,
        completionCount: c.completionCount || 0,
        tasksCount: c.tasksCount || 0,
        categoryId: c.categoryId || "",
        category: c.category || null,
        learning_order: c.learning_order,
        estimated_duration_minutes: c.estimated_duration_minutes,
        difficulty_level: c.difficulty_level,
        what_youll_learn: c.what_youll_learn,
        access_tags: c.subscription_plans?.map((p: any) => p.id) || c.access_tags || []
      }));
      setCourses(normalized);
      setCurrentPage(res.data.current_page || 1);
      setTotalPages(res.data.last_page || 1);
      setTotalCourses(res.data.total || 0);
    } catch (err) {
      console.error("Failed to fetch courses:", err);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchCourses(currentPage, searchTerm, filterTier);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, currentPage, filterTier]);

  const handleCreate = async (newCourse: any) => {
    try {
      await api.post("/courses", newCourse);
      fetchCourses(currentPage, searchTerm, filterTier);
      showToast(`Publish Complete`, `Course "${newCourse.title}" successfully integrated.`);
    } catch (err) {
      console.error("Failed to create course:", err);
      showToast(`Creation Failed`, `Unable to publish course.`, 'error');
    }
  };

  const handleUpdate = async (updatedCourse: any) => {
    try {
      await api.patch(`/courses/${updatedCourse.id}`, updatedCourse);
      setEditingCourse(null);
      fetchCourses(currentPage, searchTerm, filterTier);
      showToast(`Update Success`, `Course "${updatedCourse.title}" saved.`);
    } catch (err) {
      console.error("Failed to update course:", err);
      showToast(`Update Failed`, `Could not save properties.`, 'error');
    }
  };

  const handleDelete = (id: string) => {
    setDeleteConfirmId(id);
  };

  const confirmDelete = async () => {
    if (!deleteConfirmId) return;
    try {
      await api.delete(`/courses/${deleteConfirmId}`);
      fetchCourses(currentPage, searchTerm, filterTier);
      showToast(`Course Deleted`, `The course has been permanently removed.`);
    } catch (err: any) {
      console.error("Failed to delete course:", err);
      showToast(`Deletion Failed`, err.response?.data?.details || `Unable to remove the course.`, 'error');
    } finally {
      setDeleteConfirmId(null);
    }
  };

  const openCreate = () => {
    setEditingCourse(null);
    setIsCreatorOpen(true);
  };

  const openEdit = (course: ManagedCourse) => {
    setEditingCourse(course);
    setIsCreatorOpen(true);
  };

  return (
    <div className="space-y-6 sm:space-y-10 animate-in fade-in slide-in-from-bottom-4 duration-700 relative pb-20">
      <CourseCreator 
        isOpen={isCreatorOpen} 
        onClose={() => setIsCreatorOpen(false)} 
        onCreate={handleCreate}
        onUpdate={handleUpdate}
        initialData={editingCourse}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog 
        isOpen={deleteConfirmId !== null} 
        onClose={() => setDeleteConfirmId(null)} 
        title="Confirm Deletion"
        size="sm"
        footer={
          <div className="flex gap-4 justify-end">
            <button 
              onClick={() => setDeleteConfirmId(null)} 
              className="px-6 py-3.5 bg-surface-container-high text-on-surface rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-surface-container-highest transition-colors"
            >
              Cancel
            </button>
            <button 
              onClick={confirmDelete} 
              className="px-6 py-3.5 bg-red-500 text-white rounded-2xl font-black text-xs uppercase tracking-widest hover:bg-red-600 transition-colors shadow-lg shadow-red-500/20"
            >
              Delete Course
            </button>
          </div>
        }
      >
        <div className="text-on-surface-variant font-medium">
          <p>Are you sure you want to permanently delete this course? This action cannot be undone.</p>
        </div>
      </Dialog>

      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 pb-6 border-b border-outline-variant/10">
        <div className="space-y-1">
          <h2 className="text-2xl sm:text-3xl font-black text-on-surface tracking-tighter">
            Curriculum Hub <span className="text-primary/70 text-xl sm:text-2xl ml-1">({totalCourses})</span>
          </h2>
          <p className="text-on-surface-variant font-medium text-xs sm:text-sm">Design, monitor, and scale your educational assets.</p>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-4 w-full lg:max-w-2xl lg:ml-auto">
          <div className="relative flex-1 group w-full">
            <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant/40 group-focus-within:text-primary transition-colors">search</span>
            <input 
              type="text"
              placeholder="Filter by title or topic..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full bg-surface-container-low text-on-surface border border-outline-variant/10 rounded-2xl py-3.5 pl-12 pr-6 outline-none focus:ring-2 focus:ring-primary/20 transition-all font-medium text-xs sm:text-sm"
            />
          </div>

          <div className="relative shrink-0 group w-full sm:w-auto min-w-[160px]">
            <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant/40 group-focus-within:text-primary transition-colors pointer-events-none">filter_list</span>
            <select
              value={filterTier}
              onChange={(e) => {
                setFilterTier(e.target.value);
                setCurrentPage(1);
              }}
              className="w-full bg-surface-container-low text-on-surface border border-outline-variant/10 rounded-2xl py-3.5 pl-11 pr-10 outline-none focus:ring-2 focus:ring-primary/20 transition-all font-medium text-xs sm:text-sm appearance-none cursor-pointer"
            >
              <option value="ALL">All Plans</option>
              <option value="0">Basic / Open</option>
              {plans.map(p => (
                <option key={p.id} value={p.tier_level}>{p.name}</option>
              ))}
            </select>
            <span className="material-symbols-outlined absolute right-4 top-1/2 -translate-y-1/2 text-on-surface-variant/40 pointer-events-none text-lg">expand_more</span>
          </div>

          <button 
            onClick={openCreate}
            className="flex items-center justify-center gap-3 px-6 py-3.5 bg-primary text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all whitespace-nowrap w-full sm:w-auto"
          >
            <span className="material-symbols-outlined text-sm">add_circle</span>
            New Course
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 animate-in fade-in duration-500">
        {courses.map((course) => (
          <div 
            key={course.id}
            onClick={() => navigate(`/coach/courses/${course.id}`)}
            className="group bg-surface-container-low border border-outline-variant/10 rounded-[1.75rem] sm:rounded-[2.5rem] p-6 sm:p-8 transition-all hover:shadow-2xl hover:border-primary/45 flex flex-col justify-between gap-6 relative overflow-hidden cursor-pointer min-h-[280px]"
          >
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className={`px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border flex items-center ${
                    course.status === "PUBLISHED" ? "bg-emerald-500/10 text-emerald-500 border-emerald-500/20" : "bg-amber-500/10 text-amber-500 border-amber-500/20"
                  }`}>
                    {course.status}
                    {course.status === "PUBLISHED" && course.publishDate && (
                      <span className="opacity-70 font-bold ml-1.5 border-l border-current pl-1.5 normal-case tracking-normal text-[10px]">
                        {new Date(course.publishDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </span>
                    )}
                  </span>
                  {course.category && (
                    <button 
                      onClick={(e) => { e.stopPropagation(); navigate(`/courses?category=${encodeURIComponent(course.category!.name)}`); }}
                      className="text-[9px] font-black text-primary bg-primary/10 border border-primary/20 px-2.5 py-1 rounded-full uppercase tracking-widest flex items-center gap-1 hover:bg-primary/20 transition-colors"
                    >
                      <span className="material-symbols-outlined text-[10px]">folder</span>
                      {course.category.name}
                    </button>
                  )}
                  <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest opacity-40">ID: {course.id}</span>
                </div>
                
                <div className="flex items-center gap-2 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-all translate-x-0 sm:translate-x-2 sm:group-hover:translate-x-0">
                  <button 
                    onClick={(e) => { e.stopPropagation(); navigate(`/courses/${course.id}`); }}
                    className="w-9 h-9 rounded-xl bg-surface-container-high border border-outline-variant/10 text-on-surface-variant hover:text-primary hover:border-primary/20 transition-all shadow-sm flex items-center justify-center"
                    title="Preview Student View"
                  >
                    <span className="material-symbols-outlined text-lg">visibility</span>
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); openEdit(course); }}
                    className="w-9 h-9 rounded-xl bg-surface-container-high border border-outline-variant/10 text-on-surface-variant hover:text-primary hover:border-primary/20 transition-all shadow-sm flex items-center justify-center"
                    title="Edit Properties"
                  >
                    <span className="material-symbols-outlined text-lg">settings</span>
                  </button>
                  <button 
                    onClick={(e) => { e.stopPropagation(); handleDelete(course.id); }}
                    className="w-9 h-9 rounded-xl bg-surface-container-high border border-outline-variant/10 text-on-surface-variant hover:text-red-500 hover:border-red-500/20 transition-all shadow-sm flex items-center justify-center"
                    title="Delete"
                  >
                    <span className="material-symbols-outlined text-lg">delete</span>
                  </button>
                </div>
              </div>
              
              <div className="space-y-3">
                <h3 className="text-lg sm:text-xl font-bold text-on-surface tracking-tight leading-tight group-hover:text-primary transition-colors">
                  {course.title}
                </h3>
                <div className="flex gap-2 flex-wrap items-center">
                  {course.topics.slice(0, 2).map(topic => (
                    <button 
                      key={topic} 
                      onClick={(e) => { e.stopPropagation(); navigate(`/courses?topic=${encodeURIComponent(topic)}`); }}
                      className="text-[10px] font-bold text-on-surface-variant bg-surface-container-high px-3 py-1 rounded-full uppercase tracking-tight hover:bg-surface-container-highest transition-colors"
                    >
                      {topic}
                    </button>
                  ))}
                  {course.topics.length > 2 && (
                    <span className="text-[10px] font-bold text-on-surface-variant bg-surface-container-high px-3 py-1 rounded-full opacity-60">
                      +{course.topics.length - 2}
                    </span>
                  )}
                </div>
              </div>
              
              {(course.difficulty_level || course.estimated_duration_minutes || course.learning_order) && (
                <div className="pt-2 flex gap-2 flex-wrap mt-2">
                  {course.difficulty_level && (
                    <div className="flex items-center gap-1 text-[9px] font-bold text-on-surface-variant bg-surface-container-high px-2.5 py-1 rounded-md uppercase border border-outline-variant/5">
                      <span>
                        {course.difficulty_level === 'beginner' && '🟢'}
                        {course.difficulty_level === 'intermediate' && '🟡'}
                        {course.difficulty_level === 'advanced' && '🔴'}
                      </span>
                      <span>{course.difficulty_level}</span>
                    </div>
                  )}
                  {course.estimated_duration_minutes && (
                    <div className="flex items-center gap-1 text-[9px] font-bold text-on-surface-variant bg-surface-container-high px-2.5 py-1 rounded-md uppercase border border-outline-variant/5">
                      <span className="material-symbols-outlined text-[12px]">schedule</span>
                      <span>{formatDuration(course.estimated_duration_minutes)}</span>
                    </div>
                  )}
                  {course.learning_order && (
                    <div className="flex items-center gap-1 text-[9px] font-bold text-on-surface-variant bg-surface-container-high px-2.5 py-1 rounded-md uppercase border border-outline-variant/5">
                      <span className="material-symbols-outlined text-[12px]">format_list_numbered</span>
                      <span>Order: {course.learning_order}</span>
                    </div>
                  )}

                </div>
              )}
            </div>

            <div className="pt-4 border-t border-outline-variant/10 grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <p className="text-[9px] font-black text-on-surface-variant uppercase tracking-widest">Tasks</p>
                <p className="text-base sm:text-lg font-black text-on-surface tracking-tighter">{course.tasksCount || 0}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[9px] font-black text-on-surface-variant uppercase tracking-widest">Enrollment</p>
                <p className="text-base sm:text-lg font-black text-on-surface tracking-tighter">{course.enrollmentCount}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[9px] font-black text-on-surface-variant uppercase tracking-widest">Completed</p>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <p className="text-base sm:text-lg font-black text-emerald-500 tracking-tighter">{course.completionCount}</p>
                  <span className="text-[10px] font-bold text-on-surface-variant opacity-60">({Math.round((course.completionCount/(course.enrollmentCount || 1)) * 100)}%)</span>
                </div>
              </div>
            </div>

            <div className="absolute bottom-4 right-8 opacity-0 group-hover:opacity-40 transition-opacity hidden sm:block">
              <span className="material-symbols-outlined text-4xl text-primary -rotate-45">arrow_forward</span>
            </div>
          </div>
        ))}

        {courses.length === 0 && (
          <div className="col-span-full py-20 text-center bg-surface-container-low border border-dashed border-outline-variant/10 rounded-[2rem] sm:rounded-[3rem] px-6">
            <span className="material-symbols-outlined text-6xl text-on-surface-variant/20 mb-4">search_off</span>
            <p className="text-sm font-black text-on-surface-variant uppercase tracking-[0.2em]">No matches found for your search</p>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between pt-6 border-t border-outline-variant/10">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="px-5 py-3 bg-surface-container-high rounded-xl disabled:opacity-50 text-sm font-bold flex items-center gap-2 hover:bg-surface-container-highest transition-colors"
          >
            <span className="material-symbols-outlined text-sm">arrow_back</span>
            Previous
          </button>
          <span className="text-sm font-bold text-on-surface-variant tracking-wider">
            PAGE {currentPage} OF {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="px-5 py-3 bg-surface-container-high rounded-xl disabled:opacity-50 text-sm font-bold flex items-center gap-2 hover:bg-surface-container-highest transition-colors"
          >
            Next
            <span className="material-symbols-outlined text-sm">arrow_forward</span>
          </button>
        </div>
      )}
    </div>
  );
}
